/** How to add the MCP server to one AI tool: short steps, and a snippet to copy if it takes one. */
export interface ConnectTool {
  readonly id: string;
  readonly name: string;
  readonly steps: readonly string[];
  readonly snippet?: { readonly label: string; readonly text: string };
}

/**
 * Setup for the AI tools that reach a remote MCP server and sign in with OAuth, each checked
 * against its own documentation on 2026-10-07. `name` is the server's name in the tool, and
 * `url` its address.
 */
export function connectTools(url: string, name: string): readonly ConnectTool[] {
  return [
    {
      id: 'claude-ai',
      name: 'claude.ai',
      steps: [
        'Open Customize, then Connectors, then Add custom connector.',
        `Name it ${name} and paste the address.`,
        'Add it, then sign in and allow access.',
        'On a Team or Enterprise plan, an owner adds it in Organization settings first.',
      ],
    },
    {
      id: 'claude-code',
      name: 'Claude Code',
      steps: ['Run this command.', 'Run /mcp in Claude Code and sign in.'],
      snippet: {
        label: 'Claude Code command',
        text: `claude mcp add --transport http --scope user ${name} ${url}`,
      },
    },
    {
      id: 'chatgpt',
      name: 'ChatGPT',
      steps: [
        'It needs a paid plan, on the web, with developer mode turned on in Settings.',
        `Add a custom MCP server named ${name}, with the address and OAuth sign-in.`,
        'Sign in and allow access.',
        'On Business or Enterprise, an admin allows developer mode first.',
      ],
    },
    {
      id: 'cursor',
      name: 'Cursor',
      steps: ['Add this to ~/.cursor/mcp.json.', "Open Cursor's MCP settings and sign in."],
      snippet: {
        label: 'Cursor configuration',
        text: JSON.stringify({ mcpServers: { [name]: { url } } }, null, 2),
      },
    },
    {
      id: 'vscode',
      name: 'VS Code (GitHub Copilot)',
      steps: [
        'Add this to .vscode/mcp.json, or run MCP: Add Server.',
        'Start the server and sign in when asked.',
      ],
      snippet: {
        label: 'VS Code configuration',
        text: JSON.stringify({ servers: { [name]: { type: 'http', url } } }, null, 2),
      },
    },
    {
      id: 'codex',
      name: 'Codex CLI',
      steps: ['Run these commands; the second signs you in.'],
      snippet: {
        label: 'Codex CLI commands',
        text: `codex mcp add ${name} --url ${url}\ncodex mcp login ${name}`,
      },
    },
    {
      id: 'gemini',
      name: 'Gemini CLI',
      steps: ['Run this command.', `Run /mcp auth ${name} in Gemini CLI and sign in.`],
      snippet: {
        label: 'Gemini CLI command',
        text: `gemini mcp add --transport http --scope user ${name} ${url}`,
      },
    },
  ];
}
